import {
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@material-ui/core'
import DeleteOutline from '@material-ui/icons/DeleteOutline'
import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Container } from '../../components'
import { User } from '../../models'
import { typographyStyle } from '../../theme/typographyStyle'
import { SliceAssignModal, SliceAssignOption } from './SliceAssignModal'
import {
    deleteVendorSubAssignRule,
    fetchVendorSubAssignRules,
    VendorSubAssignRule,
} from './subAssignRulesApi'

interface Props {
    apartmentList: SliceAssignOption[]
    userList: User[]
    userListLoading: boolean
}

export const SubsRulesTab = ({
    apartmentList,
    userList,
    userListLoading,
}: Props) => {
    const theme = useTheme()
    const [rules, setRules] = useState<VendorSubAssignRule[]>([])
    const [loading, setLoading] = useState(true)
    const [modalOpen, setModalOpen] = useState(false)

    const loadRules = useCallback(async () => {
        setLoading(true)
        try {
            const data = await fetchVendorSubAssignRules()
            setRules(data)
        } catch {
            toast.error('Could not load assignment rules.')
            setRules([])
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void loadRules()
    }, [loadRules])

    const handleDelete = async (rule: VendorSubAssignRule) => {
        try {
            await deleteVendorSubAssignRule(rule.id)
            setRules((prev) => prev.filter((r) => r.id !== rule.id))
            toast.success('Rule removed.')
        } catch {
            toast.error('Could not delete rule.')
        }
    }

    return (
        <Container direction="column" style={{ width: '100%' }}>
            <Container
                style={{
                    alignItems: 'center',
                    marginBottom: theme.spacing(2),
                    width: '100%',
                }}
            >
                <Typography
                    style={{
                        ...typographyStyle(theme.typography.body2),
                        color: theme.palette.text.secondary,
                        flex: 1,
                    }}
                >
                    When the property assigns new work for a service, matching
                    rules automatically assign it to that teammate.
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setModalOpen(true)}
                    style={{ textTransform: 'none' }}
                >
                    Add rule
                </Button>
            </Container>
            {loading ? (
                <CircularProgress size={28} />
            ) : rules.length === 0 ? (
                <Paper style={{ padding: theme.spacing(2) }}>
                    <Typography color="textSecondary">
                        No rules yet. Add one to auto-assign paint at a property
                        to a sub.
                    </Typography>
                </Paper>
            ) : (
                <Paper>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Property</TableCell>
                                <TableCell>Service</TableCell>
                                <TableCell>Sub</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rules.map((rule) => (
                                <TableRow key={rule.id}>
                                    <TableCell>
                                        {rule.apartment_name || '—'}
                                    </TableCell>
                                    <TableCell>
                                        {rule.service_name || '—'}
                                    </TableCell>
                                    <TableCell>
                                        {rule.sub_vendor_name || '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            aria-label="Delete rule"
                                            onClick={() => {
                                                void handleDelete(rule)
                                            }}
                                        >
                                            <DeleteOutline fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}
            <SliceAssignModal
                apartmentList={apartmentList}
                alwaysSaveRule
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSuccess={() => {
                    void loadRules()
                }}
                userList={userList}
                userListLoading={userListLoading}
            />
        </Container>
    )
}
